// Pomodoro no app (P0.5): liga com o ícone, conta o bloco, pausa a tarefa no fim do
// foco (a pausa não conta no tempo da tarefa), toca som e mostra notificação.
import { createContext, createElement, useContext, useSyncExternalStore, type ReactNode } from 'react';
import { buildBoard, pendingForToday } from '../domain/ordering';
import {
  freezeFocus,
  idlePomodoro,
  readyToRelax,
  skipBreak,
  startBreak,
  startFocus,
  stopPomodoro,
  tickPomodoro,
  withConfig,
  type BreakMode,
  type PomodoroState,
} from '../domain/pomodoro';
import type { TFunction } from 'i18next';
import { formatDuration } from '../i18n/format';
import type { Notifier } from '../platform/notify';
import type { SoundPlayer } from '../platform/sound';
import type { AppStore, StoreEvent } from './AppStore';

const STORAGE_KEY = 'beedoit.pomodoro';

export interface PomodoroDeps {
  store: AppStore;
  sound: SoundPlayer;
  notify: Notifier;
  t: (key: string, vars?: Record<string, unknown>) => string;
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
}

export function createPomodoroController({ store, sound, notify, t, storage }: PomodoroDeps) {
  const profile = () => store.get().profile;
  const cfg = () => ({
    focus: profile()?.pomodoro_focus_min ?? 25,
    brk: profile()?.pomodoro_break_sec ?? 300,
  });
  let state: PomodoroState = load() ?? idlePomodoro(cfg().focus, cfg().brk);
  const listeners = new Set<() => void>();

  function load(): PomodoroState | null {
    try {
      const raw = storage?.getItem(STORAGE_KEY);
      const saved = raw ? (JSON.parse(raw) as PomodoroState) : null;
      // estado salvo por versões antigas (descanso em minutos) é descartado
      return saved && Number.isFinite(saved.breakSec) ? saved : null;
    } catch {
      return null;
    }
  }

  function set(next: PomodoroState) {
    if (next === state) return;
    state = next;
    try {
      storage?.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      /* fica só na memória */
    }
    listeners.forEach((l) => l());
  }

  const enabled = () => !!profile()?.pomodoro_enabled;
  const mode = (): BreakMode => profile()?.pomodoro_break_mode ?? 'block';
  /** próxima da fila (a mesma do "Iniciar próxima" da lista) */
  const nextTask = () => buildBoard(store.get().tasks, new Date(store.now())).next;

  /**
   * Finalizou no meio do bloco (AC-071): a próxima de hoje herda o bloco e inicia na hora —
   * o tempo dela conta desde a finalização. Sem próxima de hoje, o bloco acaba.
   */
  function continueBlock(base: PomodoroState) {
    const next = nextTask();
    const today = next && pendingForToday(store.get().tasks, new Date(store.now())).some((t) => t.id === next.id);
    if (!next || !today || state.phase !== 'focus' || state.endsAt == null) return set(stopPomodoro(base));
    set({ ...base, taskId: next.id });
    void store.start(next.id, 'pomodoro').then((ok) => {
      if (!ok && state.taskId === next.id) set(stopPomodoro(state));
    });
  }

  const onStoreEvent = (e: StoreEvent) => {
    if (!enabled()) return;
    const c = cfg();
    const base = withConfig(state, c.focus, c.brk);
    // Fim do dia (AC-070): sem "Vamos relaxar" nem pausa pendente — a barra comemora, some e
    // a lista volta com o pop-up, que decide se o Pomodoro continua ligado.
    if (e.type === 'finished' && store.get().celebration?.endOfDay) return set(stopPomodoro(base));
    if (mode() === 'task') {
      // Descanso ao finalizar cada tarefa (AC-069): sem blocos de foco.
      if (e.type === 'started' && state.phase !== 'idle') set(stopPomodoro(base));
      if (e.type === 'finished') set(readyToRelax(base));
      return;
    }
    if (e.type === 'started') set(startFocus(base, e.id, store.now()));
    // Pausa feita à mão congela o bloco (ASM-031 → decisão do usuário).
    if (e.type === 'paused' && e.source !== 'pomodoro' && state.taskId === e.id) set(freezeFocus(state, store.now()));
    if (e.type === 'finished' && state.taskId === e.id) continueBlock(base);
  };

  async function tick() {
    if (!enabled()) {
      if (state.phase !== 'idle') set(stopPomodoro(state));
      return;
    }
    const { state: next, event } = tickPomodoro(state, store.now());
    set(next);
    if (event === 'focusEnded' && next.taskId) {
      await store.pause(next.taskId, 'pomodoro');
      sound.play('focusEnd');
      await notify(t('pomodoro.focusEndedTitle'), t('pomodoro.focusEndedBody', { duration: formatDuration(next.breakSec, t as TFunction) }));
    }
    if (event === 'breakEnded') {
      sound.play('breakEnd');
      await notify(t('pomodoro.backToFocusTitle'), t('pomodoro.backToFocusBody'));
    }
  }

  return {
    get: () => state,
    subscribe(l: () => void) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    tick,
    /**
     * Liga (com as escolhas do pop-up: quando e quanto descansar — AC-068) ou desliga.
     * Desligar volta ao timer normal sem perder o tempo da tarefa (AC-065).
     */
    async setEnabled(on: boolean, choices?: { breakSec: number; mode: BreakMode; focusMin?: number }) {
      const ok = await store.updateProfile({
        pomodoro_enabled: on,
        ...(on && choices ? { pomodoro_break_sec: choices.breakSec, pomodoro_break_mode: choices.mode } : {}),
        ...(on && choices?.focusMin ? { pomodoro_focus_min: choices.focusMin } : {}),
      });
      if (!ok) return false;
      const c = cfg();
      set(stopPomodoro(withConfig(state, c.focus, c.brk)));
      if (!on || mode() === 'task') return true;
      const running = store.get().tasks.find((x) => x.status === 'running');
      if (running) set(startFocus(idlePomodoro(c.focus, c.brk), running.id, store.now()));
      return true;
    },
    /**
     * "Voltar ao foco" (ou "Pular descanso" na hora de relaxar): no modo por bloco retoma a
     * tarefa com um bloco novo; no modo por tarefa inicia a próxima da fila (ASM-032).
     */
    async resume() {
      const id = mode() === 'task' ? nextTask()?.id : state.taskId;
      set(stopPomodoro(state));
      if (id) await store.start(id, 'pomodoro');
    },
    /** "Vamos relaxar": começa o descanso (AC-062 / AC-069). */
    relax() {
      set(startBreak(state, store.now()));
    },
    skipBreak() {
      set(skipBreak(state));
    },
    /**
     * Passa a ouvir iniciar/pausar/finalizar. Fica fora da criação para o controlador
     * poder ser criado durante o render (o React pode criar e descartar cópias).
     */
    connect() {
      return store.on(onStoreEvent);
    },
    run(intervalMs = 1000) {
      const timer = setInterval(() => void tick(), intervalMs);
      return () => clearInterval(timer);
    },
    dispose() {
      listeners.clear();
    },
  };
}

export type PomodoroController = ReturnType<typeof createPomodoroController>;

const PomodoroContext = createContext<PomodoroController | null>(null);

export function PomodoroProvider({ controller, children }: { controller: PomodoroController; children: ReactNode }) {
  return createElement(PomodoroContext.Provider, { value: controller }, children);
}

export function usePomodoro(): { state: PomodoroState; controller: PomodoroController } {
  const controller = useContext(PomodoroContext);
  if (!controller) throw new Error('PomodoroProvider ausente');
  const state = useSyncExternalStore(controller.subscribe, controller.get, controller.get);
  return { state, controller };
}
